/**
 * client/src/lib/say.ts — the only way a family-facing string reaches a screen.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY A WRAPPER AROUND `fill()` AND NOT JUST `fill()`
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `fill()` in `shared/voice/copy.ts` **throws on an unfilled hole**, and its header explains why at
 * length: the alternative is `Making {name}'s voice.` in 32 px in front of a 68-year-old, and that
 * class of defect survives every review because it only appears when the value is missing at
 * runtime, which is exactly the case nobody screenshotted.
 *
 * That header then names the other half of the deal, and this file is it:
 *
 *   "VOX-LAW 16b then catches the throw in the studio and degrades it to
 *    `FAMILY.mishap.keepGoing`, so the cost of being strict here is one warm sentence and a line on
 *    `/admin`, and the cost of being lax is `Making {name}'s voice.` in 32 px."
 *
 * So `fill()` is strict, `say()` is the catch, and **no page calls `fill()` directly.** A page that
 * calls `fill()` is a page with an uncaught throw one missing prop away from a white screen, on the
 * path where a white screen is a phone call to Karti.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT IT DEGRADES TO, AND WHY IT IS NOT ALWAYS THE SAME SENTENCE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `FAMILY.mishap.keepGoing` ("We've got that one — keep going.") is right *mid-session* and wrong as
 * the title of the door. A degraded string still has to be a sentence that makes sense where it is
 * standing, so `say()` takes the fallback as an argument and every caller on a family page passes
 * one. The default is `keepGoing` because the studio is where this fires.
 *
 * ⚠️ **It never falls back to the raw template.** Rendering `"Making {name}'s voice."` is the exact
 * outcome the strictness was bought to prevent, and "well at least she sees something" is how it
 * gets re-introduced.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE REPORT SIDE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * A degraded string is a defect that produced no error anywhere, which is the failure mode this
 * whole repo is organised against. `onCopyFailure` is a sink the console side installs: `/live`
 * shows it to Karti with the template and the missing key, in full. Until `<Live>` mounts it is
 * `console.error`, which is not nothing — it is the difference between finding this in the first
 * session and finding it in the fifth.
 */

import { CopyError, FAMILY, fill } from "../../../shared/voice/copy.ts";
import type { Placeholder } from "../../../shared/voice/copy.ts";

export interface CopyFailure {
  template: string;
  detail: string;
}

type Sink = (f: CopyFailure) => void;

let sink: Sink = (f) => {
  // Not a `console.warn`: a hole in the copy is a defect of the same severity as a wire error, and
  // warnings are the level people filter out.
  console.error(`copy: ${f.detail} — template ${JSON.stringify(f.template)}`);
};

/**
 * Install the report sink. `/live` calls this so a copy defect reaches the person on the video call
 * rather than a devtools console nobody has open on a laptop in a living room.
 *
 * Returns the previous sink, so a component can restore it on unmount rather than leaking a closure
 * over an unmounted tree — which would keep `/live`'s whole event array alive for the rest of the
 * page's life.
 */
export function setCopyFailureSink(next: Sink): Sink {
  const prev = sink;
  sink = next;
  return prev;
}

/**
 * `say(FAMILY.wait.title, { name })` → `"Making Ma's voice."`, or the fallback if `name` is missing.
 *
 * Passing a template with no holes is free and is the normal case — most of `FAMILY` is plain
 * sentences — so there is no second function for that and no decision at the call site about which
 * one to use. One function means the grep for "does every family string go through the guard" has
 * one answer.
 */
export function say(
  template: string,
  vars: Partial<Record<Placeholder, string | number>> = {},
  fallback: string = FAMILY.mishap.keepGoing,
): string {
  try {
    return fill(template, vars);
  } catch (err) {
    if (err instanceof CopyError) {
      sink({ template, detail: err.message });
      return fallback;
    }
    throw err;
  }
}
